// Client HTTP do Service Level. Portado do ca-service-level trocando fetch pelo
// axios do iexp: token, baseURL e tratamento de sessão expirada já vêm dos
// interceptors globais (src/app/modules/auth/core/AuthHelpers.ts).
import axios from 'axios';
import {measureServiceLevelAction} from './analytics';
import {ServiceLevelPermissions} from './permissions';
import {
  FiltrosPedidos, JanelaRef, KpisJanelas, PedidoItem, PedidoRow, Periodo, Recorte,
  Regiao, Seller, SerieDia, SignoffLinha, Usuario,
} from './types';

const BASE = '/service-level';

export const HOJE: string = new Date().toISOString().slice(0, 10);

type Metodo = 'GET' | 'POST' | 'PUT' | 'DELETE';

// silencioso: não acende o overlay global (chamadas longas, ex. o sync)
async function http<T>(path: string, metodo: Metodo = 'GET', body?: unknown, silencioso = false): Promise<T> {
  try {
    const res = await axios.request({
      url: `${BASE}${path}`,
      method: metodo,
      data: body,
      ...(silencioso ? { __loading: false } : {}),
    } as any);
    return res.data as T;
  } catch (e: any) {
    // as rotas do módulo usam status HTTP (o resto do iexp usa {success:false})
    const status = e?.response?.status;
    if (status === 401) window.location.href = '/auth';
    throw new Error(e?.response?.data?.error || `Erro ${status || ''}`.trim());
  }
}

function qs(params: Record<string, unknown>): string {
  const clean = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return clean.length ? '?' + new URLSearchParams(clean.map(([k, v]) => [k, String(v)])).toString() : '';
}

function refParams(ref: JanelaRef, custom?: Periodo) {
  return { janela_ref: ref, de: custom?.de, ate: custom?.ate };
}

// ---------- Escopo do usuário ----------
export interface MeuEscopo {
  acesso: boolean;
  usuario: { nome: string; email: string };
  is_admin: boolean;
  permissions?: ServiceLevelPermissions;
  detail_level: 1 | 2 | 3;
  escopo_industrias: number[] | null;
  escopo_bus: number[] | null;
  escopo_sellers: number[] | null;
  industrias: { id: number; nome: string }[];
  bus: { id: number; nome: string; industria_id: number }[];
}

export type ServiceLevelActivity =
  | 'dashboard_view' | 'seller_view' | 'industria_view' | 'pedido_view'
  | 'sync_view' | 'sync_details_view' | 'sla_sellers_view' | 'regioes_slas_view' | 'pedido_export';

export async function recordServiceLevelActivity(
  action: ServiceLevelActivity, data: {run_id?: number; success?: boolean} = {}
): Promise<void> {
  try {
    await axios.request({
      url: `${BASE}/activity`, method: 'POST', data: {...data, action}, __loading: false,
    } as any);
  } catch (_) { /* Logs opcionais não bloqueiam a navegação nem os modais. */ }
}

export async function getMeuEscopo(): Promise<MeuEscopo> {
  return http('/me');
}

// ---------- Meta ----------
export async function getFoto(): Promise<{ ts_foto: string | null }> {
  return http('/meta/foto');
}

// ---------- Dashboard: sign-off ----------
export interface SignoffResponse {
  ts_foto: string | null;
  total: SignoffLinha;
  linhas: SignoffLinha[];
  detail_level?: number;
}

export async function getSignoff(recorte: Recorte, ref: JanelaRef, custom?: Periodo, industrias?: number[]): Promise<SignoffResponse> {
  return http(`/dashboard/signoff${qs({ recorte, industrias: industrias?.length ? industrias.join(',') : undefined, ...refParams(ref, custom) })}`);
}

export async function getIndustrias(): Promise<{ id: number; nome: string }[]> {
  return http('/industrias');
}

// ---------- Indústria ----------
export interface IndustriaResumo {
  ts_foto: string | null;
  industria: { id: number; nome: string };
  bu: { id: number; sigla: string; nome: string } | null;
  kpis: KpisJanelas;
  serie_ns: SerieDia[];
  por_bu: { bu: { id: number; sigla: string; nome: string }; kpis: KpisJanelas }[];
  sellers: { seller: Seller; kpis: KpisJanelas }[];
}

export async function getIndustriaResumo(id: number, ref: JanelaRef, custom?: Periodo, buId?: number | null): Promise<IndustriaResumo> {
  return http(`/industrias/${id}/resumo${qs({ bu: buId, ...refParams(ref, custom) })}`);
}

// ---------- Seller ----------
export interface SellerResumo {
  ts_foto: string | null;
  seller: Seller;
  industria: { id: number; nome: string } | null;
  kpis: KpisJanelas;
  serie_ns: SerieDia[];
  problemas: {
    p1: { pedidos: number; hoje: number };
    p2: { pedidos: number; hoje: number };
    p3: { valor: number; pedidos: number; hoje: number };
  };
}

export async function getSellerResumo(id: number, ref: JanelaRef, custom?: Periodo, industriaId?: number | null): Promise<SellerResumo> {
  return http(`/sellers/${id}/resumo${qs({ industria: industriaId, ...refParams(ref, custom) })}`);
}

export async function getSellerPedidos(sellerId: number, f: FiltrosPedidos = {}): Promise<{ ts_foto: string | null; total: number; pedidos: PedidoRow[] }> {
  return http(`/sellers/${sellerId}/pedidos${qs({ ...f })}`);
}

export async function getPedidosParaExport(sellerId: number, f: FiltrosPedidos = {}): Promise<PedidoRow[]> {
  const rows = await http<PedidoRow[]>(`/sellers/${sellerId}/pedidos/export${qs({ ...f })}`);
  return rows.map((r) => ({ ...r, industrias_nomes: r.industrias_nomes ?? [] }));
}

// ---------- Pedido ----------
export interface PedidoDetalhe {
  ts_foto: string | null;
  visao_restrita: string[] | null;
  pedido: PedidoRow;
  seller: Seller;
  lojista: { razao_social: string; cnpj: string; cidade: string; uf: string };
  sla: { prazo_horas: number; tipo_contagem: string; configurado: boolean };
  itens: PedidoItem[];
}

export async function getPedido(pedidoId: string): Promise<PedidoDetalhe> {
  return http(`/pedidos/${encodeURIComponent(pedidoId)}`);
}

// ---------- Regiões (leitura, usada nos filtros) ----------
export async function getRegioes(): Promise<Regiao[]> {
  return http('/admin/regioes');
}

// ---------- Admin ----------






export interface SlaMatrix {
  sellers: Seller[];
  regioes: Regiao[];
  slas: { id: number; seller_id: number; regiao_id: number; prazo_horas: number; tipo_contagem: 'CORRIDAS' | 'UTEIS'; vigencia_inicio: string }[];
  default_horas: number;
}

export async function getSlaMatrix(): Promise<SlaMatrix> {
  return http('/admin/slas');
}

// SLA simplificado: prazo em DIAS aplicado a todas as regiões do seller de uma vez
export async function salvarSlaSeller(sellerId: number, dias: number, tipo: 'CORRIDAS' | 'UTEIS' = 'CORRIDAS') {
  return measureServiceLevelAction('sla_seller',
    () => http('/admin/slas/seller', 'POST', { seller_id: sellerId, dias, tipo_contagem: tipo }),
    {seller_id: sellerId, dias, tipo_contagem: tipo});
}

export async function salvarSla(sellerId: number, regiaoId: number, prazoHoras: number, tipo: 'CORRIDAS' | 'UTEIS') {
  return measureServiceLevelAction('sla_region',
    () => http('/admin/slas', 'POST', {
      seller_id: sellerId, regiao_id: regiaoId, prazo_horas: prazoHoras, tipo_contagem: tipo,
    }), {seller_id: sellerId, regiao_id: regiaoId, prazo_horas: prazoHoras, tipo_contagem: tipo});
}

// ---------- Sync (admin) ----------
export interface SyncRun {
  id: number;
  status: 'RUNNING' | 'OK' | 'ERRO';
  detalhe: string | null;
  iniciado_em: string;
  fim_em: string | null;
  ts_foto?: string | null;
  estimativa_ms?: number | null;
  estimativa_amostras?: number | null;
}

export async function getSyncStatus(): Promise<{ ts_foto: string | null; runs: SyncRun[] }> {
  return http('/admin/sync', 'GET', undefined, true);
}

export async function dispararSync(): Promise<{ ok: boolean; detalhe?: string; erro?: string }> {
  return measureServiceLevelAction('sync',
    () => http<{ok: boolean; detalhe?: string; erro?: string}>(
      '/admin/sync', 'POST', undefined, true)); // silencioso: leva minutos
}

export interface LogAuditoria {
  id: number;
  usuario: string;
  acao: string;
  entidade: string;
  detalhe: string;
  ts: string;
}

export async function getAuditoria(): Promise<LogAuditoria[]> {
  return http('/admin/auditoria');
}
